import { ClientWorkFlowStatus } from "@wingspanhq/payments/dist/interfaces";
import { WSQueryCache } from "@ws-react-query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../BulkActions";
import {
  QUERY_PAYABLES as QUERY_PAYABLES_NEW,
  QUERY_PAYABLES_SUMMARY
} from "../../../query/payables/keys";
import {
  QUERY_PAYABLE,
  QUERY_PAYABLES as QUERY_PAYABLES_LEGACY
} from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { Payable } from "../../../services/api/payments/payables";

const invalidateAfterMutation = (successItems: Payable[]) => {
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_NEW);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_LEGACY);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_SUMMARY);
  successItems.forEach(payable => {
    if (payable.payableId) {
      WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable.payableId]);
    }
  });
};

const processApprovePayables = (
  items: Payable[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> => {
  const approvable = items.filter(item => item.status !== "Paid");
  const skipped = items.length - approvable.length;

  const bulkAction = bulkActionFactory<Payable>({
    onProgress,
    action: async item => {
      if (!item.payableId) {
        throw new Error("Payable is missing payableId");
      }
      return paymentsService.payable.update(item.payableId, {
        client: { workflowStatus: ClientWorkFlowStatus.Approved }
      });
    },
    onFinish: ({ successItems }) => {
      invalidateAfterMutation(successItems);
      onSuccess?.();
    }
  });

  return bulkAction.run(approvable).then(({ successItems, failedItems }) => ({
    total: items.length,
    processed: successItems.length + failedItems.length + skipped,
    successful: successItems.length,
    failed: failedItems.length
  }));
};

export const useBulkApprovePayables = () => {
  const runBulkAction = useForegroundBulkAction();

  return (items: Payable[], onSuccess?: () => void) => {
    const pluralized = `payable${items.length > 1 ? "s" : ""}`;

    return runBulkAction({
      actionName: "Approve payables",
      itemCount: items.length,
      confirmDescription: `Approve ${pluralized} to be paid`,
      confirmLabel: "Approve",
      prepare: async () => items,
      process: async (data, onProgress) =>
        processApprovePayables(data, onProgress, onSuccess)
    });
  };
};
